import { useTranslation } from 'react-i18next';
import type { MessageItem } from '@/mocks/dashboard';

interface Props {
  messages: MessageItem[];
}

export default function DashboardMessages({ messages }: Props) {
  const { t } = useTranslation('dashboard');
  const hasUnread = messages.some((m) => m.unread);

  return (
    <div className={`rounded-xl border bg-white ${hasUnread ? 'border-warm-300 ring-1 ring-warm-100' : 'border-ink-100'}`}>
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-ink-100">
        <div className="flex items-center gap-2">
          <i className="ri-message-3-line text-warm-400 text-lg" />
          <h3 className="text-sm md:text-base font-semibold text-ink-600">{t('dashboard.messages.title')}</h3>
          {hasUnread && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">
              {messages.filter((m) => m.unread).length}
            </span>
          )}
        </div>
        <a href="/my/messages" className="text-xs md:text-sm font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap">
          {t('dashboard.seeAll')}
        </a>
      </div>

      {messages.length === 0 ? (
        <div className="px-4 md:px-5 py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-message-3-line text-cream-400 text-xl" />
          </div>
          <p className="text-sm text-ink-300">{t('dashboard.messages.empty')}</p>
        </div>
      ) : (
        <div className="divide-y divide-ink-100">
          {messages.map((m) => (
            <div key={m.id} className="px-4 md:px-5 py-3 md:py-4 flex items-start gap-3">
              <div className="shrink-0 mt-0.5">
                {m.unread ? (
                  <div className="w-2.5 h-2.5 rounded-full bg-warm-500" />
                ) : (
                  <div className="w-2.5 h-2.5 rounded-full bg-cream-200" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm truncate ${m.unread ? 'font-semibold text-ink-600' : 'font-medium text-ink-500'}`}>
                    {m.title}
                  </p>
                </div>
                {m.relatedNumber && (
                  <p className="text-[11px] text-warm-400 mt-0.5">
                    {t('dashboard.messages.related')}: {m.relatedNumber}
                  </p>
                )}
                <p className="text-xs text-ink-300 mt-1 line-clamp-1">{m.preview}</p>
                <p className="text-[11px] text-ink-200 mt-1">{new Date(m.date).toLocaleDateString()}</p>
              </div>
              <a
                href={`/my/messages/${m.id}`}
                className="shrink-0 text-xs font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap mt-0.5"
              >
                {t('dashboard.messages.open')}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}